import { useState, useCallback } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { useAuth } from '@/auth/AuthProvider';
import { useCurrency } from '@/contexts/currency';
import { SubscriptionService } from '@/services/billing/SubscriptionService';

export const useStripeCheckout = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { userProfile } = useAuth();
  const { currencyConfig } = useCurrency();

  const checkout = useCallback(async (quantity?: number) => {
    setLoading(true);
    setError(null);

    const STRIPE_PK = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY;

    if (!STRIPE_PK) {
      setError("Stripe is not configured correctly");
      setLoading(false);
      return;
    }

    try {
      const sessionId = await SubscriptionService.createCheckoutSession({
        email: userProfile?.email || null,
        userId: userProfile?.id || null,
        customerId: userProfile?.stripe_id || null,
        quantity,
        priceId: currencyConfig.stripePriceIds.professional,
      });

      const stripe = await loadStripe(STRIPE_PK);
      if (!stripe) throw new Error("Stripe failed to load");

      const { error: stripeError } = await stripe.redirectToCheckout({
        sessionId,
      });

      if (stripeError) throw new Error(stripeError.message);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "An unexpected error occurred";
      const userFriendlyMessage = errorMessage.includes("Edge Function returned a non-2xx status code")
        ? "Oops, some error occurred"
        : errorMessage;
      setError(userFriendlyMessage);
    } finally {
      setLoading(false);
    }
  }, [userProfile, currencyConfig]);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return { 
    checkout, 
    loading, 
    error, 
    clearError 
  };
};